/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { InsightComposer } from '../../pages/InsightComposer';
import { Login } from '@ink-ai/portal/insight-hub/pages/Login';
import { ThemeProvider } from '@ink-ai/portal/theme';
import App from '../../App';
import { useSelector } from 'react-redux';
import { Initializer } from '@ink-ai/portal/common/components/Initializer';
import { RootState } from '@ink-ai/portal/reducers';
import { Glossary } from '../../pages/GlossaryCollections';
import { GlossaryItemList } from '../../pages/GlossaryItemList';
import { ImportGlossary } from '../../pages/ImportGlossary';
import { Translation } from '../../pages/Translation';
import { ConnectorsSettings } from '../../pages/settings/ConnectorsSettings';
import { ImportConnector } from '../../pages/settings/ImportConnector';
import { ArticlesList } from '../../pages/ArticlesList';
import { TranslateDocumentTaskList } from '../../pages/TranslateDocumentTaskList';
import { ReferenceStore } from '../../pages/reference-store/ReferenceStoreList';
import { ImportReferenceStore } from '../../pages/reference-store/ImportReferenceStore';
import { ReferenceStoreDetail } from '../../pages/reference-store/ReferenceStoreDetail';
import { WritingInstructionsList } from '../../pages/WritingInstructionsList';
import { CreateWritingInstruction } from '../../pages/writing-instructions/CreateWritingInstruction';
import { EditWritingInstruction } from '../../pages/writing-instructions/EditWritingInstruction';
import { WritingRuleList } from '../../pages/WritingRuleList';
import { CreateWritingRule } from '../../pages/writing-rules/CreateWritingRule';
import { EditWritingRule } from '../../pages/writing-rules/EditWritingRule';
import { GlossaryExtraction } from '../../pages/GlossaryExtraction';
import { ExtractGlossaryTaskList } from '../../pages/GlossaryExtractionTaskList';
import { ReferenceExtraction } from '../../pages/ReferenceExtraction';
import { ExtractReferenceTaskList } from '../../pages/ReferenceExtractionTaskList';
import { Settings } from '../../pages/settings';
import { WorkflowList } from '../../pages/WorkflowList';
import { ImportWorkflow } from '../../pages/ImportWorkflow';
import { UpdateWorkflow } from '../../pages/UpdateWorkflow';
import { InvokeWorkflow } from '../workflow/InvokeWorkflow';
import { Home } from '../../pages/Home';
import { DocumentTranslation } from '../../pages/TranslateDocument';
import { TranslateDocumentPreview } from '../../pages/TranslateDocumentPreview';
import { XmlTranslationEditor } from '../../pages/XmlTranslationEditor';
import RequireAdmin from './RequireAdmin';
import RequireSuperAdmin from './RequireSuperAdmin';
import { NoPermission } from '../../pages/NoPermission';
import { Dashboard } from '../../pages/Dashboard';

const router = createBrowserRouter([
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/no-permission',
    element: <NoPermission />,
  },
  {
    path: '/',
    element: <App />,
    children: [
      {
        path: '/',
        element: <Home />,
      },
      {
        path: '/articles',
        element: <ArticlesList />,
      },
      {
        path: '/translate-tasks',
        element: <TranslateDocumentTaskList />,
      },
      {
        path: '/translate-tasks/:id/preview',
        element: <TranslateDocumentPreview />,
      },
      {
        path: '/translate-tasks/:id/xml-editor',
        element: <XmlTranslationEditor />,
      },
      {
        path: '/medical-writer',
        element: <InsightComposer />,
      },
      {
        path: '/articles/new',
        element: <InsightComposer />,
      },
      {
        path: '/articles/:id/edit',
        element: <InsightComposer />,
      },
      {
        path: '/text-translator',
        element: <Translation />,
      },
      {
        path: '/document-translator',
        element: <DocumentTranslation />,
      },
      {
        path: '/dashboard',
        element: (
          <RequireSuperAdmin>
            <Dashboard />
          </RequireSuperAdmin>
        ),
      },
      {
        path: '/glossary',
        element: <Glossary />,
      },
      {
        path: '/glossary-management',
        element: (
          <RequireAdmin>
            <Glossary />
          </RequireAdmin>
        )
      },
      {
        path: '/glossary/:id',
        element: <GlossaryItemList />,
      },
      {
        path: '/glossary-management/:id',
        element: (
          <RequireAdmin>
            <GlossaryItemList />
          </RequireAdmin>
        ),
      },
      {
        path: '/glossary-management/import',
        element: (
          <RequireAdmin>
            <ImportGlossary />
          </RequireAdmin>
        ),
      },
      {
        path: '/reference-store',
        element: <ReferenceStore />,
      },
      {
        path: '/reference-management',
        element: (
          <RequireAdmin>
            <ReferenceStore />
          </RequireAdmin>
        ),
      },
      {
        path: '/reference-store/:id',
        element: <ReferenceStoreDetail />,
      },
      {
        path: '/reference-management/:id',
        element: (
          <RequireAdmin>
            <ReferenceStoreDetail />
          </RequireAdmin>
        ),
      },
      {
        path: '/reference-management/import',
        element: (
          <RequireAdmin>
            <ImportReferenceStore />
          </RequireAdmin>
        ),
      },
      {
        path: '/writing-instruction',
        element: <WritingInstructionsList />,
      },
      {
        path: '/writing-instruction/new',
        element: <CreateWritingInstruction />,
      },
      {
        path: '/writing-instruction/:id/edit',
        element: <EditWritingInstruction />,
      },
      {
        path: '/writing-rule',
        element: <WritingRuleList />,
      },
      {
        path: '/writing-rule/:id/edit',
        element: <EditWritingRule />,
      },
      {
        path: '/writing-rule/new',
        element: <CreateWritingRule />,
      },
      {
        path: '/workflows',
        element: <WorkflowList />,
      },
      {
        path: '/workflows/import',
        element: <ImportWorkflow />,
      },
      {
        path: '/workflows/:id/update',
        element: <UpdateWorkflow />,
      },
      {
        path: '/settings/connectors',
        element: <ConnectorsSettings />,
      },
      {
        path: '/settings/connector/import',
        element: <ImportConnector />,
      },
      {
        path: '/extraction/glossary',
        element: (
          <RequireSuperAdmin>
            <GlossaryExtraction/>
          </RequireSuperAdmin>
        ),
      },
      {
        path: '/extraction/glossary/tasks',
        element: (
          <RequireSuperAdmin>
            <ExtractGlossaryTaskList />
          </RequireSuperAdmin>
        ),
      },
      {
        path: '/extraction/reference',
        element: (
          <RequireSuperAdmin>
            <ReferenceExtraction/>
          </RequireSuperAdmin>
        ),
      },
      {
        path: '/extraction/reference/tasks',
        element: (
          <RequireSuperAdmin>
            <ExtractReferenceTaskList />
          </RequireSuperAdmin>
        ),
      },
      {
        path: '/settings',
        element: <Settings />,
      },
      {
        path: '/workflows/:id/invoke',
        element: <InvokeWorkflow />,
      },
      {
        path: '/workflows/:id/invoke/:conversationId',
        element: <InvokeWorkflow />,
      },
    ],
  },
]);

export const AppRouter = () => {
  const app = useSelector((state: RootState) => state.app);
  return (
    <>
      {app.isInitializing ? (
        <Initializer />
      ) : (
        <ThemeProvider>
          <RouterProvider router={router} />
        </ThemeProvider>
      )}
    </>
  );
};
